import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Compass, BookOpen, Crown, MapPin, Lock, Check, X, Sparkles, Loader2, Trophy, Skull, Send, Star, Coins } from 'lucide-react';
import { toast } from 'sonner';
import PokemonCaptureAnimation from '../components/PokemonCaptureAnimation';
import { fetchWithRetry } from '../utils/silentRetry';
import { readCache, writeCache } from '../utils/journeyCache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────────────
// HUB: list of active journeys for the student
// ─────────────────────────────────────────────────────────────────────
export default function StudentJourneys() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = 'hub:list';

    // 1) Show cached payload instantly if present (offline-first feel).
    const cached = readCache(cacheKey);
    if (cached?.data) {
      setItems(cached.data.journeys || []);
      setLoading(false);
    }

    // 2) Revalidate from server with silent retry on transient failures.
    (async () => {
      try {
        const r = await fetchWithRetry(
          `${BACKEND_URL}/api/student/journeys`,
          { credentials: 'include' },
          { retries: 3 }
        );
        if (!r.ok) throw new Error('Falha ao carregar jornadas');
        const d = await r.json();
        if (cancelled) return;
        setItems(d.journeys || []);
        writeCache(cacheKey, d);
      } catch (e) {
        // If we already painted a cached copy, stay silent — students keep playing.
        if (!cached?.data) toast.error(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-amber-50 to-emerald-50 dark:from-slate-900 dark:to-slate-800" data-testid="student-journeys-page">
      <nav className="bg-white/80 dark:bg-slate-900/80 backdrop-blur border-b border-slate-200 dark:border-slate-700 sticky top-0 z-30">
        <div className="container mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/student/dashboard')} data-testid="back-dashboard-btn">
            <ArrowLeft className="w-5 h-5"/>
          </Button>
          <div>
            <h1 className="font-outfit font-black text-xl flex items-center gap-2">
              <Compass className="w-6 h-6 text-fuchsia-500"/> Jornadas
            </h1>
            <p className="text-xs text-slate-500">Campanhas narrativas criadas pelo seu professor</p>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="w-10 h-10 animate-spin mx-auto text-fuchsia-500"/>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 bg-white/70 dark:bg-slate-800/70 rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-600">
            <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-3"/>
            <p className="font-outfit font-black text-lg text-slate-700 dark:text-slate-100">Nenhuma jornada ativa</p>
            <p className="text-sm text-slate-500 italic mt-1">Quando seu professor publicar uma jornada, ela aparecerá aqui.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="student-journeys-grid">
            {items.map((j) => {
              const completed = j.progress?.completed?.length || 0;
              const total = j.num_missions || 0;
              const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
              const finalized = j.progress?.finalized;
              return (
                <button
                  key={j.journey_id}
                  type="button"
                  onClick={() => navigate(`/student/journeys/${j.journey_id}`)}
                  data-testid={`journey-card-${j.journey_id}`}
                  className="text-left bg-white dark:bg-slate-800 rounded-2xl border-[3px] border-slate-900 shadow-[3px_3px_0_rgba(15,23,42,0.85)] p-4 hover:-translate-y-0.5 active:translate-y-0 transition-all"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-outfit font-black text-lg text-slate-900 dark:text-slate-50 flex-1">{j.title}</h3>
                    {finalized ? (
                      <Badge className="bg-emerald-500 text-white"><Check className="w-3 h-3 mr-1"/>Concluída</Badge>
                    ) : completed > 0 ? (
                      <Badge className="bg-amber-400 text-slate-900">Em curso</Badge>
                    ) : (
                      <Badge variant="secondary">Nova</Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                    <span className="font-bold">{total}</span> missões · gênero <strong>{j.genre}</strong>
                  </p>
                  {(j.starts_at || j.ends_at) && (
                    <p className="text-[11px] text-slate-600 dark:text-slate-300 mb-2 italic" data-testid={`journey-window-hint-${j.journey_id}`}>
                      {j.starts_at && j.ends_at
                        ? `Aberta ${new Date(j.starts_at).toLocaleString('pt-BR', {day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})} → ${new Date(j.ends_at).toLocaleString('pt-BR', {day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})}`
                        : j.starts_at
                          ? `A partir de ${new Date(j.starts_at).toLocaleString('pt-BR', {day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})}`
                          : `Até ${new Date(j.ends_at).toLocaleString('pt-BR', {day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})}`}
                    </p>
                  )}
                  <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-fuchsia-500 to-amber-400 transition-all" style={{ width: `${pct}%` }}/>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">{completed}/{total} missões</p>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// PLAY page: mission map + active mission dialog
// ─────────────────────────────────────────────────────────────────────
export function StudentJourneyPlay() {
  const navigate = useNavigate();
  const { journeyId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMission, setActiveMission] = useState(null);   // mission being played
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);                 // submission outcome
  const [showReview, setShowReview] = useState(false);        // gabarito review dialog

  const reload = async () => {
    const cacheKey = `play:${journeyId}`;

    // Cache-first: paint last-known-good immediately so the map doesn't go blank
    // if the server is momentarily slow or the network blinks.
    const cached = readCache(cacheKey);
    if (cached?.data && !data) {
      setData(cached.data);
      setLoading(false);
    }

    try {
      const r = await fetchWithRetry(
        `${BACKEND_URL}/api/student/journeys/${journeyId}`,
        { credentials: 'include' },
        { retries: 3 }
      );
      if (!r.ok) throw new Error('Jornada não disponível');
      const d = await r.json();
      setData(d);
      writeCache(cacheKey, d);
    } catch (e) {
      // Silent fallback: if we had a cached copy, keep playing; otherwise bounce out.
      if (!cached?.data) {
        toast.error(e.message);
        navigate('/student/journeys');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [journeyId]);

  if (loading || !data) {
    return (
      <div className="min-h-screen grid place-items-center bg-gradient-to-br from-violet-50 to-amber-50">
        <Loader2 className="w-10 h-10 animate-spin text-fuchsia-500"/>
      </div>
    );
  }

  const missions = data.missions || [];
  const progress = data.progress || {};
  const allDone = missions.length > 0 && missions.every((m) => m.completed);

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-amber-50 to-emerald-50 dark:from-slate-900 dark:to-slate-800 pb-12" data-testid="student-journey-play">
      <nav className="bg-white/80 dark:bg-slate-900/80 backdrop-blur border-b border-slate-200 dark:border-slate-700 sticky top-0 z-30">
        <div className="container mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/student/journeys')} data-testid="back-journeys-btn">
            <ArrowLeft className="w-5 h-5"/>
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-outfit font-black text-lg sm:text-xl truncate">{data.title}</h1>
            <p className="text-[11px] text-slate-500">Gênero: {data.genre} · {missions.length} missões</p>
          </div>
        </div>
      </nav>

      {/* Story intro */}
      {data.story_intro && (
        <div className="container mx-auto px-4 pt-5">
          <div className="rounded-2xl border-[3px] border-slate-900 shadow-[3px_3px_0_rgba(15,23,42,0.85)] bg-white dark:bg-slate-800 p-4">
            <p className="text-xs font-outfit font-black uppercase tracking-widest text-fuchsia-500 mb-1">Abertura</p>
            <p className="font-nunito text-slate-800 dark:text-slate-100 leading-relaxed text-sm sm:text-base">{data.story_intro}</p>
          </div>
        </div>
      )}

      {/* Status banner — closed journey unlocks the review/gabarito */}
      {data.status === 'closed' && (
        <div className="container mx-auto px-4 pt-3">
          <button
            type="button"
            onClick={() => setShowReview(true)}
            className="w-full bg-gradient-to-r from-amber-400 to-rose-400 hover:from-amber-500 hover:to-rose-500 border-[3px] border-slate-900 shadow-[3px_3px_0_rgba(15,23,42,0.85)] rounded-2xl px-4 py-3 text-left flex items-center gap-3 transition-all active:translate-y-0.5"
            data-testid="open-review-btn"
          >
            <span className="text-3xl">🗝️</span>
            <div className="flex-1">
              <p className="font-outfit font-black text-slate-900">Gabarito liberado!</p>
              <p className="text-xs text-slate-700">Sua professora encerrou esta jornada. Toque para revisar suas respostas e ver as explicações.</p>
            </div>
            <span className="text-2xl">→</span>
          </button>
        </div>
      )}

      {/* Map view — visual layout of the journey */}
      <div className="container mx-auto px-4 pt-5">
        <StudentJourneyMap
          mapImageUrl={data.map_image_url}
          missions={missions}
          numMissions={data.num_missions}
          onMissionClick={(m) => { setResult(null); setActiveMission(m); }}
        />
      </div>

      {/* Mission path */}
      <div className="container mx-auto px-4 py-5">
        <div className="space-y-3" data-testid="mission-path">
          {missions.map((m, idx) => {
            const isCompleted = m.completed;
            const isUnlocked = m.unlocked;
            const isClimax = m.is_climax || (m.order === data.num_missions);
            const Icon = isClimax ? Crown : MapPin;
            return (
              <div key={m.mission_id} className="flex items-stretch gap-3" data-testid={`mission-row-${idx}`}>
                {/* Connector */}
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full border-[3px] border-slate-900 grid place-items-center font-outfit font-black text-sm ${
                    isCompleted ? 'bg-emerald-400 text-white' :
                    isUnlocked  ? 'bg-amber-300 text-slate-900' :
                                  'bg-slate-200 text-slate-400'
                  }`}>
                    {isCompleted ? <Check className="w-5 h-5"/> : isUnlocked ? <Icon className="w-5 h-5"/> : <Lock className="w-4 h-4"/>}
                  </div>
                  {idx < missions.length - 1 && (
                    <div className={`flex-1 w-1 my-1 ${isCompleted ? 'bg-emerald-400' : 'bg-slate-200'}`}/>
                  )}
                </div>

                {/* Card */}
                <button
                  type="button"
                  disabled={!isUnlocked || isCompleted}
                  onClick={() => { setResult(null); setActiveMission(m); }}
                  data-testid={`mission-card-${idx}`}
                  className={`flex-1 text-left rounded-2xl border-[3px] border-slate-900 p-3 transition-all ${
                    isCompleted ? 'bg-emerald-50 dark:bg-emerald-900/30' :
                    isUnlocked  ? 'bg-white dark:bg-slate-800 shadow-[3px_3px_0_rgba(15,23,42,0.85)] hover:-translate-y-0.5 active:translate-y-0' :
                                  'bg-slate-100 dark:bg-slate-800/40 opacity-60'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className={`font-outfit font-black text-sm sm:text-base ${isClimax ? 'text-amber-600' : 'text-slate-900 dark:text-slate-50'}`}>
                      {isClimax && '👑 '} {m.title}
                    </p>
                    {isCompleted && <Badge className="bg-emerald-500 text-white">Concluída</Badge>}
                    {!isCompleted && !isUnlocked && <Badge variant="secondary"><Lock className="w-3 h-3 mr-1"/>Bloqueada</Badge>}
                    {isUnlocked && !isCompleted && <Badge className="bg-amber-400 text-slate-900">Disponível</Badge>}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
                    <span className="flex items-center gap-1"><Star className="w-3 h-3 text-amber-500"/>{m.xp_reward} AURA</span>
                    <span className="flex items-center gap-1"><Coins className="w-3 h-3 text-amber-500"/>{m.coin_reward}</span>
                    <span>· {m.bloom_level}</span>
                  </div>
                </button>
              </div>
            );
          })}
        </div>

        {/* Climax once all done */}
        {allDone && (
          <ClimaxBlock journey={data} progress={progress}/>
        )}
      </div>

      {/* Active mission dialog */}
      <MissionDialog
        open={!!activeMission}
        mission={activeMission}
        onClose={() => { setActiveMission(null); setResult(null); }}
        journeyId={journeyId}
        submitting={submitting}
        setSubmitting={setSubmitting}
        result={result}
        setResult={setResult}
        onAfterSubmit={reload}
      />

      {/* Review (gabarito) — only after the teacher closes the journey */}
      <JourneyReviewDialog
        open={showReview}
        journeyId={journeyId}
        onClose={() => setShowReview(false)}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Visual map: shows the teacher-designed map with mission pins,
// linking them with a dashed path and color-coded statuses.
// ─────────────────────────────────────────────────────────────────────
function StudentJourneyMap({ mapImageUrl, missions, numMissions, onMissionClick }) {
  const bg = mapImageUrl || '/maps/default-quest-map.jpg';
  // If no positions yet, distribute on the same default S-curve as the editor
  const positioned = missions.map((m, i) => {
    const has = typeof m.map_x === 'number' && typeof m.map_y === 'number';
    if (has) return m;
    const t = missions.length > 1 ? i / (missions.length - 1) : 0.5;
    const x = 15 + t * 70;
    const y = 80 - Math.sin(t * Math.PI) * 55;
    return { ...m, map_x: x, map_y: y };
  });

  return (
    <div
      className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden border-[3px] border-slate-900 shadow-[3px_3px_0_rgba(15,23,42,0.85)] bg-slate-200 select-none"
      style={{
        backgroundImage: `url(${bg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      data-testid="student-journey-map"
    >
      {/* Dashed path */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 100 100">
        {positioned.length > 1 && (
          <polyline
            points={positioned.map((m) => `${m.map_x},${m.map_y}`).join(' ')}
            fill="none"
            stroke="#0f172a"
            strokeWidth="0.6"
            strokeDasharray="1.5 1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.9"
          />
        )}
      </svg>

      {/* Mission pins */}
      {positioned.map((m, i) => {
        const isClimax = m.is_climax || (m.order === numMissions);
        const isCompleted = m.completed;
        const isUnlocked = m.unlocked;
        const disabled = !isUnlocked || isCompleted;
        return (
          <button
            key={m.mission_id}
            type="button"
            data-testid={`map-mission-pin-${i}`}
            disabled={disabled}
            onClick={() => !disabled && onMissionClick(m)}
            style={{ left: `${m.map_x}%`, top: `${m.map_y}%` }}
            className={`absolute -translate-x-1/2 -translate-y-full ${disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-110'} transition-transform`}
          >
            <div className="relative flex flex-col items-center">
              <div className={`rounded-full border-[3px] border-slate-900 shadow-[2px_2px_0_rgba(15,23,42,0.85)] grid place-items-center w-9 h-9 ${
                isCompleted ? 'bg-emerald-400' :
                isUnlocked  ? (isClimax ? 'bg-amber-400 animate-pulse' : 'bg-rose-500 animate-pulse') :
                              'bg-slate-300'
              }`}>
                {isCompleted ? <Check className="w-4 h-4 text-white"/> :
                 !isUnlocked ? <Lock className="w-3.5 h-3.5 text-slate-600"/> :
                 isClimax    ? <Crown className="w-4 h-4 text-slate-900"/> :
                               <MapPin className="w-4 h-4 text-white"/>}
              </div>
              <span className="absolute -top-1 -left-1 w-4 h-4 bg-slate-900 text-white text-[9px] font-outfit font-black rounded-full grid place-items-center border-2 border-white">{i + 1}</span>
              <span className={`mt-1 px-1.5 py-0.5 rounded-md border-2 border-slate-900 text-[10px] font-outfit font-black whitespace-nowrap max-w-[140px] truncate shadow-sm ${
                isCompleted ? 'bg-emerald-50 text-emerald-800' :
                isUnlocked  ? 'bg-white/95 text-slate-900' :
                              'bg-slate-200/95 text-slate-500'
              }`}>
                {m.title || `Missão ${i + 1}`}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Climax block — shown after the last mission was completed.
// ─────────────────────────────────────────────────────────────────────
function ClimaxBlock({ journey, progress }) {
  const success = progress.final_success;
  const accuracy = Math.round((progress.final_accuracy || 0) * 100);
  return (
    <div className={`mt-6 rounded-2xl border-[3px] border-slate-900 shadow-[3px_3px_0_rgba(15,23,42,0.85)] p-4 ${success ? 'bg-emerald-50' : 'bg-rose-50'}`} data-testid="climax-block">
      <div className="flex items-center gap-2 mb-2">
        {success ? <Trophy className="w-6 h-6 text-amber-500"/> : <Skull className="w-6 h-6 text-rose-500"/>}
        <p className="font-outfit font-black text-lg">{success ? 'Jornada vencida!' : 'Jornada encerrada'}</p>
        <Badge className={success ? 'bg-emerald-500' : 'bg-rose-500'}>{accuracy}% de acertos</Badge>
      </div>
      <p className="font-nunito text-slate-800 leading-relaxed text-sm">{success ? journey.climax_success : journey.climax_failure}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Mission play dialog — question-by-question with submit at end
// ─────────────────────────────────────────────────────────────────────
function MissionDialog({ open, mission, onClose, journeyId, submitting, setSubmitting, result, setResult, onAfterSubmit }) {
  const [answers, setAnswers] = useState({});
  const [showCapture, setShowCapture] = useState(false);
  const draftKey = mission ? `journey_draft_${journeyId}_${mission.mission_id}` : null;

  // Whenever a new result arrives with a Pokémon award, trigger the capture animation
  useEffect(() => {
    if (result?.finalize?.success && result?.finalize?.award?.sprite_url) {
      setShowCapture(true);
    } else {
      setShowCapture(false);
    }
  }, [result]);

  // Restore draft when the mission changes
  useEffect(() => {
    if (!mission?.mission_id) { setAnswers({}); return; }
    try {
      const raw = localStorage.getItem(`journey_draft_${journeyId}_${mission.mission_id}`);
      if (raw) {
        const d = JSON.parse(raw);
        const stale = d.savedAt && (Date.now() - d.savedAt) > 7 * 24 * 60 * 60 * 1000;
        if (!stale) {
          setAnswers(d.answers || {});
          toast.success('Rascunho restaurado — continue de onde parou!', { icon: '💾', duration: 3000 });
          return;
        }
        localStorage.removeItem(`journey_draft_${journeyId}_${mission.mission_id}`);
      }
    } catch (e) { /* ignore */ }
    setAnswers({});
  }, [mission?.mission_id, journeyId]);

  // Autosave answers
  useEffect(() => {
    if (!draftKey || result) return;
    if (Object.keys(answers).length === 0) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify({ answers, savedAt: Date.now() }));
    } catch (e) { /* ignore */ }
  }, [draftKey, answers, result]);

  const questions = useMemo(() => mission?.questions || [], [mission]);
  const allAnswered = questions.length > 0 && questions.every((q, i) => {
    const a = answers[i];
    return q.question_type === 'multiple_choice' ? !!a : (typeof a === 'string' && a.trim().length > 0);
  });

  if (!mission) return null;

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        answers: questions.map((q, i) => ({
          question_index: i,
          answer: answers[i],
        })),
      };
      const r = await fetch(`${BACKEND_URL}/api/student/journeys/${journeyId}/missions/${mission.mission_id}/submit`, {
        method: 'POST', credentials: 'include',
        headers: {'Content-Type':'application/json'}, body: JSON.stringify(payload),
      });
      if (!r.ok) {
        let detail = 'Erro ao enviar';
        try { detail = (await r.json()).detail || detail; } catch (_) { /* ignore */ }
        if (r.status === 401 || r.status === 403) {
          throw new Error('Sua sessão expirou. Suas respostas estão salvas — faça login novamente e volte para esta missão.');
        }
        throw new Error(detail);
      }
      const data = await r.json();
      setResult(data);
      // Submission accepted (success or fail) — clear the autosaved draft
      try { if (draftKey) localStorage.removeItem(draftKey); } catch (e) { /* ignore */ }
      if (data.mission_success) {
        toast.success(`Missão concluída! +${data.xp_awarded} AURA`, { icon: '🎉' });
        // Surface the progression cascade triggered by award_xp_to_student
        const prog = data.progression;
        if (prog?.leveled_up) {
          toast.success(`Parabéns! Você subiu para o nível ${prog.level}! 🆙`, { duration: 5000 });
          if (prog.unlocked_powers?.length) {
            toast.message(`Poderes desbloqueados: ${prog.unlocked_powers.join(', ')} ⚡`, { duration: 6000 });
          }
        }
        if (prog?.evolved) {
          toast.success('Seu Pokémon evoluiu! ✨', { duration: 5000 });
        }
      } else {
        toast.message('Missão fracassou — revise o conteúdo e prossiga', { icon: '💧' });
      }
    } catch (e) {
      toast.error(e.message || 'Falha ao enviar respostas. Suas respostas estão salvas — tente novamente.', { duration: 6000 });
      // Keep the dialog open with answers intact for retry
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    onAfterSubmit && onAfterSubmit();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="mission-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit text-xl flex items-center gap-2">
            {mission.is_climax ? <Crown className="w-5 h-5 text-amber-500"/> : <MapPin className="w-5 h-5 text-violet-500"/>}
            {mission.title}
          </DialogTitle>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            {questions.length === 0 && (
              <p className="text-sm italic text-slate-500">Esta missão ainda está sendo preparada pelo professor.</p>
            )}
            {questions.map((q, i) => (
              <div key={i} className="border-2 border-slate-200 rounded-xl p-3" data-testid={`question-${i}`}>
                <p className="font-outfit font-bold text-sm mb-2">{i+1}. {q.question_text}</p>
                {q.question_type === 'multiple_choice' ? (
                  <div className="space-y-1.5">
                    {['a','b','c','d'].map((l) => (
                      <button
                        key={l}
                        type="button"
                        onClick={() => setAnswers((a) => ({ ...a, [i]: l.toUpperCase() }))}
                        data-testid={`question-${i}-option-${l.toUpperCase()}`}
                        className={`w-full text-left px-3 py-2 rounded-lg border-2 text-sm transition-all ${
                          answers[i] === l.toUpperCase()
                            ? 'bg-amber-100 border-slate-900 -translate-y-0.5 shadow-md'
                            : 'bg-white border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <strong>{l.toUpperCase()})</strong> {q[`option_${l}`]}
                      </button>
                    ))}
                  </div>
                ) : (
                  <Textarea
                    placeholder="Escreva sua resposta…"
                    value={answers[i] || ''}
                    onChange={(e) => setAnswers((a) => ({ ...a, [i]: e.target.value }))}
                    rows={4}
                    data-testid={`question-${i}-essay`}
                  />
                )}
              </div>
            ))}

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="ghost" onClick={handleClose} disabled={submitting}>Cancelar</Button>
              <Button
                onClick={submit}
                disabled={!allAnswered || submitting}
                className="bg-fuchsia-500 hover:bg-fuchsia-600 text-white"
                data-testid="submit-mission-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <Send className="w-4 h-4 mr-1"/>}
                Enviar respostas
              </Button>
            </div>
          </div>
        )}

        {/* Result panel */}
        {result && (
          <div className="space-y-4" data-testid="mission-result">
            <div className={`rounded-2xl border-2 p-4 text-center ${result.mission_success ? 'bg-emerald-50 border-emerald-300' : 'bg-rose-50 border-rose-300'}`}>
              <div className="text-3xl mb-1">{result.mission_success ? '🎉' : '💧'}</div>
              <p className="font-outfit font-black text-lg">{result.mission_success ? 'Missão Concluída!' : 'Missão fracassou'}</p>
              <p className="text-xs text-slate-600 mt-1">
                Acertos: <strong>{result.correct_count}/{result.total}</strong>
              </p>
              {result.mission_success && (
                <p className="text-xs text-slate-700 mt-1">+{result.xp_awarded} AURA · +{result.coins_awarded} moedas</p>
              )}
            </div>

            {result.story_beat && (
              <div className="rounded-xl border-2 border-slate-200 p-3 bg-white">
                <p className="text-xs font-outfit font-black uppercase tracking-widest text-fuchsia-500 mb-1">A história continua…</p>
                <p className="font-nunito text-slate-800 text-sm leading-relaxed">{result.story_beat}</p>
              </div>
            )}

            {/* Finalization & Pokémon award (only after capture animation ends) */}
            {result.finalize?.success && result.finalize?.award && !showCapture && (
              <div className="rounded-2xl border-[3px] border-amber-500 bg-gradient-to-br from-amber-50 to-emerald-50 p-4 text-center shadow-[3px_3px_0_rgba(15,23,42,0.85)]" data-testid="pokemon-award">
                <p className="text-[11px] font-outfit font-black uppercase tracking-widest text-amber-700 mb-1">Recompensa!</p>
                <img src={result.finalize.award.sprite_url} alt={result.finalize.award.name} loading="lazy" decoding="async" className="w-28 h-28 mx-auto" style={{ imageRendering: 'pixelated' }}/>
                <p className="font-outfit font-black text-lg">{result.finalize.award.name}</p>
                <p className="text-xs text-slate-600 italic">{result.finalize.award.first_capture ? 'Capturado pela primeira vez!' : 'Adicionado à sua coleção!'}</p>
              </div>
            )}
            {result.finalize?.climax_text && (
              <div className={`rounded-xl border-2 p-3 ${result.finalize.success ? 'bg-emerald-50 border-emerald-300' : 'bg-rose-50 border-rose-300'}`}>
                <p className="text-xs font-outfit font-black uppercase tracking-widest mb-1">{result.finalize.success ? 'Final triunfal' : 'Final amargo'}</p>
                <p className="font-nunito text-slate-800 text-sm leading-relaxed">{result.finalize.climax_text}</p>
              </div>
            )}

            <div className="flex justify-end pt-2 border-t">
              <Button onClick={handleClose} className="bg-amber-400 hover:bg-amber-500 text-slate-900" data-testid="close-mission-result">
                Continuar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>

      {/* Capture animation overlay — rendered at root, above the dialog */}
      {showCapture && result?.finalize?.award && (
        <PokemonCaptureAnimation
          spriteUrl={result.finalize.award.sprite_url}
          name={result.finalize.award.name}
          firstCapture={!!result.finalize.award.first_capture}
          onDone={() => setShowCapture(false)}
        />
      )}
    </Dialog>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Review dialog — student can browse the answer key after the journey is closed
// ─────────────────────────────────────────────────────────────────────
function JourneyReviewDialog({ open, journeyId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${BACKEND_URL}/api/student/journeys/${journeyId}/review`, { credentials: 'include' });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.detail || 'Erro ao carregar revisão');
        setData(d);
      } catch (e) {
        toast.error(e.message);
        onClose();
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [open, journeyId]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit text-xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500"/>
            Revisão da Jornada
          </DialogTitle>
        </DialogHeader>
        {loading || !data ? (
          <div className="py-16 text-center"><Loader2 className="w-10 h-10 animate-spin mx-auto text-amber-500"/></div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-600 italic">Confira suas respostas e veja o gabarito com a explicação de cada questão.</p>
            {data.missions.map((m, mi) => (
              <details key={m.mission_id} open={mi === 0} className="rounded-2xl border-2 border-slate-200 bg-white">
                <summary className="cursor-pointer px-3 py-2 flex items-center gap-2 text-sm font-outfit font-black text-slate-800">
                  <span className={`w-6 h-6 rounded-full grid place-items-center text-[10px] border-2 ${
                    !m.submitted ? 'bg-slate-200 border-slate-400 text-slate-500' :
                    m.success    ? 'bg-emerald-400 border-slate-900 text-white' :
                                   'bg-rose-500 border-slate-900 text-white'
                  }`}>
                    {!m.submitted ? '–' : m.success ? <Check className="w-3 h-3"/> : <X className="w-3 h-3"/>}
                  </span>
                  Missão {m.order} — {m.title}
                  {m.submitted && (
                    <span className="ml-auto text-[11px] text-slate-500">{m.correct}/{m.total}</span>
                  )}
                </summary>
                <div className="px-3 pb-3 space-y-2">
                  {!m.submitted && (
                    <p className="text-xs italic text-slate-500 px-2">Você não chegou a responder essa missão.</p>
                  )}
                  {m.questions.map((q, qi) => {
                    const isMC = q.question_type === 'multiple_choice';
                    const correct = q.correct_answer;
                    const your = q.your_answer;
                    return (
                      <div key={qi} className={`rounded-xl p-3 border-2 text-xs ${q.was_correct ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}`} data-testid={`review-q-${mi}-${qi}`}>
                        <p className="font-outfit font-bold text-sm text-slate-800 mb-2">{qi + 1}. {q.question_text}</p>
                        {isMC ? (
                          <ul className="space-y-1">
                            {['a','b','c','d'].map((l) => {
                              const letter = l.toUpperCase();
                              const isCorrect = correct === letter;
                              const isYours = your === letter;
                              return (
                                <li
                                  key={l}
                                  className={`px-2 py-1 rounded-md border ${
                                    isCorrect ? 'bg-emerald-100 border-emerald-400 text-emerald-900 font-bold' :
                                    isYours   ? 'bg-rose-100 border-rose-400 text-rose-900 font-bold' :
                                                'bg-white border-slate-200 text-slate-600'
                                  }`}
                                >
                                  <span className="mr-1">{letter})</span>
                                  {q[`option_${l}`]}
                                  {isCorrect && <span className="ml-1">✓ correta</span>}
                                  {isYours && !isCorrect && <span className="ml-1">— sua resposta</span>}
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <div className="space-y-1">
                            <p className="text-[11px] text-slate-500">Sua resposta:</p>
                            <p className="bg-white p-2 rounded-md border border-slate-200 text-slate-800">{your || '—'}</p>
                            {!!(q.expected_keywords || []).length && (
                              <p className="text-[11px] text-slate-500 italic">Palavras-chave esperadas: {q.expected_keywords.join(', ')}</p>
                            )}
                          </div>
                        )}
                        {q.justification && (
                          <div className="mt-2 rounded-md bg-white border border-slate-200 p-2 text-[11px]">
                            <p className="font-outfit font-black uppercase tracking-widest text-amber-700 mb-0.5">💡 Por quê?</p>
                            <p className="text-slate-700">{q.justification}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </details>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
